/* eslint-disable react-refresh/only-export-components */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { RefreshCw } from 'lucide-react';

import { createField, getMeta } from '@/components/forms/field.schema';
import { FormField } from '@/components/forms/FormField';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Editor } from '@/components/editor/Editor';
import { useCreateTicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { TicketsStoreProvider } from '@/components/editor/TicketPlugin/TicketsStoreProvider';
import { Viewer } from '@/components/editor/Viewer';
import { DTO, Ticket, parseTicketDto } from '@certhon/models';

export const DataReloadContext = React.createContext<{
  reloadTrigger: number;
  triggerReload: () => void;
}>({
  reloadTrigger: 0,
  triggerReload: () => {},
});

const testTicketDTOs: DTO<Ticket>[] = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    recnum: 1,
    number: 1001,
    title: 'Investigate login issue',
    description: 'Users intermittently see a 500 on login.',
    status: 'opened',
    due: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    labels: [1, 2],
    close_date: null,
    project_id: 10,
    client_id: null,
    werk_id: null,
    assignee_user_id: 2,
    creator_user_id: 1,
    executor_user_id: null,
    created: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    modified: new Date().toISOString(),
    notification_opt_in: [1, 2],
    notification_opt_out: [],
    deleted: false,
    ticket_references: [],
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    recnum: 2,
    number: 1002,
    title: 'Add export to CSV',
    description: 'Implement CSV export for reports page.',
    status: 'opened',
    due: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    labels: [3],
    close_date: null,
    project_id: 10,
    client_id: null,
    werk_id: 20,
    assignee_user_id: 3,
    creator_user_id: 1,
    executor_user_id: 4,
    created: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    modified: new Date().toISOString(),
    notification_opt_in: [],
    notification_opt_out: [],
    deleted: false,
    ticket_references: [],
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    recnum: 3,
    number: 1003,
    title: 'Refactor ticket service',
    description: 'Break up monolithic service into smaller modules.',
    status: 'closed',
    due: null,
    labels: [],
    close_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    project_id: 11,
    client_id: null,
    werk_id: null,
    assignee_user_id: 2,
    creator_user_id: 1,
    executor_user_id: 2,
    created: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    modified: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    notification_opt_in: [],
    notification_opt_out: [3],
    deleted: false,
    ticket_references: [],
  },
  {
    id: '00000000-0000-0000-0000-000000000004',
    recnum: 4,
    number: 1004,
    title: 'Update dependencies',
    description: 'Bump minor versions and verify build passes.',
    status: 'opened',
    due: null,
    labels: [5],
    close_date: null,
    project_id: null,
    client_id: null,
    werk_id: null,
    assignee_user_id: null,
    creator_user_id: 1,
    executor_user_id: null,
    created: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    modified: new Date().toISOString(),
    notification_opt_in: [],
    notification_opt_out: [],
    deleted: false,
    ticket_references: [],
  },
  {
    id: '00000000-0000-0000-0000-000000000005',
    recnum: 5,
    number: 1005,
    title: 'Improve ticket search',
    description: 'Add fuzzy matching and filters.',
    status: 'opened',
    due: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    labels: [2, 4],
    close_date: null,
    project_id: 10,
    client_id: null,
    werk_id: 30,
    assignee_user_id: 4,
    creator_user_id: 1,
    executor_user_id: 4,
    created: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    modified: new Date().toISOString(),
    notification_opt_in: [],
    notification_opt_out: [],
    deleted: false,
    ticket_references: [],
  },
];

function useSimulatedData<T>(
  data: T[],
  reloadTrigger: number,
  delay: { min: number; max: number },
) {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log(
      `SimulatedData: Purging and reloading, trigger: ${reloadTrigger}`,
    );
    setIsLoading(true);
    setItems([]);

    const timeout = setTimeout(
      () => {
        console.log('SimulatedData: Loading complete');
        setItems(data);
        setIsLoading(false);
      },
      Math.random() * (delay.max - delay.min) + delay.min,
    );

    return () => clearTimeout(timeout);
  }, [data, reloadTrigger, delay.min, delay.max]);

  return { items, isLoading };
}

// Test schema for RichTextField
const testField = createField(z.string(), {
  fieldType: 'richtext',
  label: 'Rich Text Description',
  placeholder: 'Enter your description...',
});

const testSchema = z.object({
  description: testField,
});

type TestFormData = z.infer<typeof testSchema>;

export function EditorDemo() {
  const [markdown, setMarkdown] = useState(`# Lexical WYSIWYG Editor Demo

This is a demo of a **Lexical-based** [WYSIWYG editor](https://lexical.dev/) with the following features:

- Rich text editing with toolbar
- Markdown import/export
- @ mentions (try typing @john or @alice)
- # ticket references (try typing #task)
- Lists and other formatting

## Features

1. **Bold**, *italic*, ~~strikethrough~~ text
2. Different heading levels
3. Bullet points and numbered lists
4. Task lists with checkboxes
5. > Blockquotes for emphasis

## Task List Example

- [x] Completed task
- [ ] Pending task
- [ ] Another pending task

Hello @alice! Try typing @ or # to see the autocomplete in action!

See ticket #1001 for the login issue, or #1003+ for more details.
also: #00000000-0000-0000-0000-000000000005+`);

  const [reloadTrigger, setReloadTrigger] = useState(0);

  const triggerReload = useCallback(() => {
    setReloadTrigger((prev) => prev + 1);
  }, []);

  const { items: ticketDTOs, isLoading: ticketsLoading } = useSimulatedData(
    testTicketDTOs,
    reloadTrigger,
    { min: 800, max: 1800 },
  );

  const tickets = React.useMemo(
    () => ticketDTOs.map(parseTicketDto),
    [ticketDTOs],
  );

  const ticketsStore = useCreateTicketStore(tickets, ticketsLoading);

  // Test form for RichTextField
  const form = useForm<TestFormData>({
    resolver: zodResolver(testSchema),
    defaultValues: {
      description:
        '# Sample Content\n\nThis is **test content** for the rich text field!',
    },
  });

  const processedField = {
    name: 'description',
    meta: getMeta(testField)!,
    isOptional: false,
  };

  const onSubmit = (data: TestFormData) => {
    console.log('Form submitted:', data);
    alert('Form submitted! Check console for data.');
  };

  return (
    <div className="container mx-auto max-w-4xl p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Editor Demo</h1>
            <p className="text-muted-foreground mt-2">
              Rich text editor implementation and features
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={triggerReload}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reload Data
          </Button>
        </div>
      </div>

      <DataReloadContext.Provider value={{ reloadTrigger, triggerReload }}>
        <TicketsStoreProvider store={ticketsStore}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Schema-Driven RichTextField Test</CardTitle>
            </CardHeader>
            <CardContent>
              <FormProvider {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField field={processedField} formMode="edit" />
                  <div className="flex gap-2">
                    <Button type="submit">Submit</Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => form.reset()}
                    >
                      Reset
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Direct Editor (Original)</CardTitle>
            </CardHeader>
            <CardContent>
              <Editor
                namespace="editor"
                key={reloadTrigger}
                defaultValue={markdown}
                onChange={setMarkdown}
              />
            </CardContent>
          </Card>
        </TicketsStoreProvider>
      </DataReloadContext.Provider>
      <Separator className="my-4" />
      <Card>
        <CardHeader>
          <CardTitle>Read-Only Viewer</CardTitle>
        </CardHeader>
        <CardContent>
          <DataReloadContext.Provider value={{ reloadTrigger, triggerReload }}>
            <TicketsStoreProvider store={ticketsStore}>
              <Viewer key={`${markdown}-${reloadTrigger}`} value={markdown} />
            </TicketsStoreProvider>
          </DataReloadContext.Provider>
        </CardContent>
      </Card>
      <Separator className="my-4" />
      <Card>
        <CardHeader>
          <CardTitle>Markdown Output</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted overflow-x-auto rounded-md p-4 text-sm whitespace-pre-wrap">
            {markdown}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
